import * as React from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Content interface for ServiceCardSearch component.
 * All text content is passed via props to enable CMS integration.
 */
export interface ServiceCardSearchContent {
  /** Placeholder text displayed in the input field */
  placeholder: string;
  /** Accessible label for screen readers (visually hidden) */
  label: string;
  /** Accessible label for the clear button */
  clearButtonLabel: string;
}

/**
 * Props interface for ServiceCardSearch component.
 */
export interface ServiceCardSearchProps {
  /** Content configuration for the search input */
  content: ServiceCardSearchContent;
  /** Current search value (controlled component) */
  value: string;
  /** Callback when the search value changes */
  onChange: (value: string) => void;
  /** Callback when the user submits the search (presses Enter) */
  onSubmit: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * ServiceCardSearch - Search input component for filtering service cards.
 *
 * This is a controlled input component that triggers search on Enter key press,
 * not real-time filtering. Includes an optional clear button when value is present.
 *
 * @example
 * ```tsx
 * import { ServiceCardSearch } from '@/components';
 *
 * const [searchValue, setSearchValue] = useState('');
 *
 * <ServiceCardSearch
 *   content={{
 *     placeholder: "Search...",
 *     label: "Search services",
 *     clearButtonLabel: "Clear search"
 *   }}
 *   value={searchValue}
 *   onChange={setSearchValue}
 *   onSubmit={() => handleSearch(searchValue)}
 * />
 * ```
 */
export const ServiceCardSearch = React.forwardRef<
  HTMLInputElement,
  ServiceCardSearchProps
>(({ content, value, onChange, onSubmit, className }, ref) => {
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Merge refs if external ref is provided
  React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  /**
   * Handle key down events - submit on Enter
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onSubmit();
    }
  };

  /**
   * Handle input change
   */
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  /**
   * Handle clear button click
   */
  const handleClear = () => {
    onChange('');
    // Focus the input after clearing
    inputRef.current?.focus();
  };

  const hasValue = value.length > 0;

  return (
    <div className={cn('relative w-full', className)}>
      {/* Accessible label (visually hidden) */}
      <label htmlFor="service-card-search" className="sr-only">
        {content.label}
      </label>

      {/* Search input */}
      <input
        ref={inputRef}
        id="service-card-search"
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={content.placeholder}
        className={cn(
          'flex h-10 w-full rounded-md border border-input bg-background',
          'pl-3 pr-16 py-2 text-sm text-foreground',
          'ring-offset-background',
          'placeholder:text-muted-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50'
        )}
        aria-describedby={hasValue ? 'service-card-search-clear' : undefined}
      />

      {/* Icons container on the right side */}
      <div className="absolute inset-y-0 right-0 flex items-center gap-1 pr-3">
        {/* Clear button - only shown when there's a value */}
        {hasValue && (
          <button
            id="service-card-search-clear"
            type="button"
            onClick={handleClear}
            className={cn(
              'p-1 rounded-sm',
              'text-muted-foreground hover:text-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'transition-colors'
            )}
            aria-label={content.clearButtonLabel}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}

        {/* Search icon */}
        <Search
          className="h-5 w-5 text-muted-foreground"
          aria-hidden="true"
        />
      </div>
    </div>
  );
});

ServiceCardSearch.displayName = 'ServiceCardSearch';
